import {
  BranchNotFoundError,
  RepositoryAlreadyExistsError,
  RepositoryNotFoundError,
  RevisionNotFoundError,
  type DeleteTagResult,
  type GarbageCollectionPlan,
  type GraphIdentity,
  type BranchRecord,
  type Head,
  type RepositoryStorageEstimate,
  type RevisionNumber,
  type RevisionSummary,
  type TagRecord,
} from "@bjalon/object-vcs-core";
import {
  collectionGroup,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { firestore } from "../lib/firebase";
import type { Formation } from "../types/formation";
import type {
  Deck,
  Exercise,
  PedagogicalContentKind,
  PedagogicalContentLists,
  Questionnaire,
} from "../types/pedagogicalContent";
import { asRecord } from "../utils/firestoreData";
import { readFormation } from "./formationService";
import {
  FORMATION_CONTENT_DEFAULT_BRANCH,
  qualiopiQuestionnaireModelRefSchema,
  questionnaireUsageSchema,
  type FormationContentState,
  type QualiopiQuestionnaireModelRef,
  type QuestionnaireUsage,
  type TrainerProfileSnapshot,
} from "../versioning/formationContentGraph";
import {
  FORMATION_CONTENT_CURRENT_PUBLICATION_TAG,
  createFormationContentPersistence,
  createFormationContentRepository,
  type FormationContentRepository,
} from "../versioning/formationContentRepository";
import { writeAuditEvent } from "./auditService";

export const CURRENT_PUBLICATION_TAG = FORMATION_CONTENT_CURRENT_PUBLICATION_TAG;

type DeckWithQualiopiRefs = Deck & {
  readonly qualiopiQuestionnaireModelRefs?: unknown;
};

type QuestionnaireWithUsage = Questionnaire & {
  readonly usage?: unknown;
};

export type FormationContentSnapshotInput = {
  readonly capturedAt?: Date;
  readonly contents: PedagogicalContentLists;
  readonly formation: Formation;
  readonly program?: FormationContentState["program"];
  readonly trainerProfiles?: readonly TrainerProfileSnapshot[];
};

export type FormationContentSnapshotSyncResult = {
  readonly head: Head<FormationContentState>;
  readonly initialized: boolean;
  readonly revision: RevisionSummary | null;
  readonly state: FormationContentState;
};

export type FormationContentHistory = {
  readonly branches: readonly BranchRecord[];
  readonly garbageCollectionPlan: GarbageCollectionPlan | null;
  readonly graphIdentity: GraphIdentity;
  readonly head: Head<FormationContentState> | null;
  readonly revisions: readonly RevisionSummary[];
  readonly storageEstimate: RepositoryStorageEstimate | null;
  readonly tags: readonly TagRecord[];
};

export type PublishedFormationContentSnapshot = {
  readonly revision: RevisionNumber;
  readonly state: FormationContentState;
  readonly tag: TagRecord;
};

export type FormationPublicationSnapshotResult = {
  readonly revision: RevisionNumber | null;
  readonly schemaFingerprint: string;
  readonly tagName: string;
};

export type FormationPublicationFreezeResult = FormationPublicationSnapshotResult & {
  readonly created: boolean;
};

export function contentPublicationTagName({
  contentId,
  kind,
  versionId,
}: {
  contentId: string;
  kind: PedagogicalContentKind;
  versionId: string;
}): string {
  return [
    "published",
    kind,
    sanitizeTagPart(contentId),
    sanitizeTagPart(versionId),
  ].join("-");
}

export function buildFormationContentState({
  capturedAt = new Date(),
  contents,
  formation,
  program = null,
  trainerProfiles = [],
}: FormationContentSnapshotInput): FormationContentState {
  const capturedAtIso = capturedAt.toISOString();
  const decks: FormationContentState["decks"] = {};
  const questionnaires: FormationContentState["questionnaires"] = {};
  const exercises: FormationContentState["exercises"] = {};
  const trainerProfileRecords: FormationContentState["trainerProfiles"] = {};

  for (const deck of contents.decks) {
    decks[deck.id] = deckSnapshot(deck);
  }

  for (const questionnaire of contents.questionnaires) {
    questionnaires[questionnaire.id] = questionnaireSnapshot(questionnaire);
  }

  for (const exercise of contents.exercises) {
    exercises[exercise.id] = exerciseSnapshot(exercise);
  }

  for (const trainerProfile of trainerProfiles) {
    trainerProfileRecords[trainerProfile.uid] = trainerProfile;
  }

  return {
    decks,
    exercises,
    formation: {
      authorUid: formation.authorUid,
      capturedAtIso,
      description: formation.description,
      formationId: formation.id,
      schemaVersion: 1,
      sourceUpdatedAtIso: toIso(formation.updatedAt),
      title: formation.title,
    },
    program,
    questionnaires,
    trainerProfiles: trainerProfileRecords,
  };
}

export async function syncFormationContentSnapshot({
  actorUid,
  formationId,
  message = "Snapshot contenu formation",
}: {
  readonly actorUid: string;
  readonly formationId: string;
  readonly message?: string;
}): Promise<FormationContentSnapshotSyncResult> {
  const formation = await readFormation(formationId);

  if (!formation) {
    throw new Error("Formation introuvable.");
  }

  const { listPedagogicalContent } = await import("./pedagogicalContentService");
  const contents = await listPedagogicalContent(formationId);
  const state = buildFormationContentState({
    contents,
    formation,
  });
  const repository = createFormationContentRepository(formationId);

  return writeFormationContentSnapshot({
    actorUid,
    message,
    repository,
    state,
  });
}

export async function readFormationContentHistory(
  formationId: string,
): Promise<FormationContentHistory> {
  const repository = createFormationContentRepository(formationId);
  const graphIdentity = repository.getGraphIdentity();

  try {
    const [head, revisions, tags, branches, storageEstimate, garbageCollectionPlan] =
      await Promise.all([
      repository.getHead({ branch: FORMATION_CONTENT_DEFAULT_BRANCH }),
      repository.listRevisions({ limit: 50, order: "desc" }),
      repository.listTags(),
      repository.listBranches(),
      estimateFormationContentStorage(formationId),
      repository.planGarbageCollection({
        estimateStorage: true,
        includeOrphanBlobs: true,
      }),
    ]);

    return {
      branches,
      garbageCollectionPlan,
      graphIdentity,
      head,
      revisions,
      storageEstimate,
      tags,
    };
  } catch (error) {
    if (
      error instanceof BranchNotFoundError ||
      error instanceof RepositoryNotFoundError
    ) {
      return {
        branches: [],
        garbageCollectionPlan: null,
        graphIdentity,
        head: null,
        revisions: [],
        storageEstimate: null,
        tags: [],
      };
    }

    throw error;
  }
}

export async function commitFormationPublicationSnapshot({
  actorUid,
  content,
  formationId,
  message,
  tagName,
}: {
  readonly actorUid: string;
  readonly content: PedagogicalContentLists;
  readonly formationId: string;
  readonly message: string;
  readonly tagName: string;
}): Promise<FormationPublicationSnapshotResult> {
  const formation = await readFormation(formationId);

  if (!formation) {
    throw new Error("Formation introuvable pour le snapshot versionné.");
  }

  const repo = createFormationContentRepository(formationId);
  const state = buildFormationContentState({
    contents: content,
    formation,
  });
  const initialized = await ensureRepositoryInitialized({
    actorUid,
    message,
    repo,
    state,
  });

  if (!initialized) {
    const head = await repo.getHead({ branch: FORMATION_CONTENT_DEFAULT_BRANCH });
    await repo.update(() => state, {
      author: actorUid,
      branch: FORMATION_CONTENT_DEFAULT_BRANCH,
      commit: true,
      expectedHeadHash: head.stateHash,
      message,
    });
  }

  await repo.tag(CURRENT_PUBLICATION_TAG, {
    author: actorUid,
    branch: FORMATION_CONTENT_DEFAULT_BRANCH,
    overwrite: true,
    revision: "HEAD",
  });
  const tag = await repo.tag(tagName, {
    author: actorUid,
    branch: FORMATION_CONTENT_DEFAULT_BRANCH,
    overwrite: true,
    revision: "HEAD",
  });

  return {
    revision: tag.revision,
    schemaFingerprint: repo.getGraphIdentity().schemaFingerprint,
    tagName: tag.name,
  };
}

export async function freezeCurrentFormationPublicationSnapshot({
  actorUid,
  formationId,
  message,
}: {
  readonly actorUid: string;
  readonly formationId: string;
  readonly message: string;
}): Promise<FormationPublicationFreezeResult> {
  const { listPedagogicalContent } = await import("./pedagogicalContentService");
  const result = await commitFormationPublicationSnapshot({
    actorUid,
    content: await listPedagogicalContent(formationId),
    formationId,
    message,
    tagName: `published-formation-${sanitizeTagPart(formationId)}`,
  });

  return {
    ...result,
    created: true,
  };
}

export async function ensureCurrentFormationPublicationSnapshot({
  actorUid,
  formationId,
  message,
}: {
  readonly actorUid: string;
  readonly formationId: string;
  readonly message: string;
}): Promise<FormationPublicationFreezeResult> {
  try {
    const snapshot = await readCurrentPublishedFormationContentSnapshot(formationId);
    const repository = createFormationContentRepository(formationId);

    return {
      created: false,
      revision: snapshot.revision,
      schemaFingerprint: repository.getGraphIdentity().schemaFingerprint,
      tagName: snapshot.tag.name,
    };
  } catch (error) {
    if (
      error instanceof Error &&
      error.message.includes("aucun snapshot publié")
    ) {
      return freezeCurrentFormationPublicationSnapshot({
        actorUid,
        formationId,
        message,
      });
    }

    throw error;
  }
}

export async function deleteFormationContentTag({
  actorUid,
  expectedRevision,
  formationId,
  tagName,
}: {
  readonly actorUid: string;
  readonly expectedRevision: RevisionNumber;
  readonly formationId: string;
  readonly tagName: string;
}): Promise<DeleteTagResult> {
  const repository = createFormationContentRepository(formationId);
  const tags = await repository.listTags();
  const tag = tags.find((candidate) => candidate.name === tagName);

  if (!tag) {
    throw new Error("Tag introuvable.");
  }

  if (tag.name === CURRENT_PUBLICATION_TAG) {
    throw new Error("Le tag de publication courant ne peut pas être supprimé.");
  }

  if (tag.revision !== expectedRevision) {
    throw new Error("La révision attendue du tag ne correspond plus.");
  }

  const currentPublicationTag = tags.find(
    (candidate) => candidate.name === CURRENT_PUBLICATION_TAG,
  );

  if (currentPublicationTag?.revision === tag.revision) {
    throw new Error(
      "Ce tag pointe vers la publication courante et ne peut pas être supprimé.",
    );
  }

  const isReferenced = await isFormationContentRevisionReferencedBySession({
    formationId,
    revision: tag.revision,
  });

  if (isReferenced) {
    throw new Error("Ce tag pointe vers une révision référencée par une session.");
  }

  const result = await repository.deleteTag(tag.name, {
    author: actorUid,
    expectedRevision,
  });

  await writeAuditEvent({
    actorUid,
    eventType: "formation.content_tag.deleted",
    metadata: {
      formationId,
      previousRevision: result.previousRevision,
      tagName,
    },
  });

  return result;
}

export async function readCurrentPublishedFormationContentSnapshot(
  formationId: string,
): Promise<PublishedFormationContentSnapshot> {
  const repository = createFormationContentRepository(formationId);

  try {
    const tags = await repository.listTags();
    const tag = tags.find(
      (candidate) => candidate.name === FORMATION_CONTENT_CURRENT_PUBLICATION_TAG,
    );

    if (!tag) {
      throw new Error(
        `La formation ${formationId} n'a aucun snapshot publié. Publiez au moins une version de contenu avant de démarrer la session.`,
      );
    }

    return {
      revision: tag.revision,
      state: await repository.readRevision(tag.revision),
      tag,
    };
  } catch (error) {
    if (
      error instanceof BranchNotFoundError ||
      error instanceof RepositoryNotFoundError ||
      error instanceof RevisionNotFoundError
    ) {
      throw new Error(
        `La formation ${formationId} n'a aucun snapshot publié exploitable. Publiez au moins une version de contenu avant de démarrer la session.`,
      );
    }

    throw error;
  }
}

async function writeFormationContentSnapshot({
  actorUid,
  message,
  repository,
  state,
}: {
  readonly actorUid: string;
  readonly message: string;
  readonly repository: FormationContentRepository;
  readonly state: FormationContentState;
}): Promise<FormationContentSnapshotSyncResult> {
  try {
    await repository.getHead({ branch: FORMATION_CONTENT_DEFAULT_BRANCH });
  } catch (error) {
    if (!(error instanceof BranchNotFoundError)) {
      throw error;
    }

    const result = await repository.init({
      author: actorUid,
      branch: FORMATION_CONTENT_DEFAULT_BRANCH,
      commit: true,
      initialState: state,
      message,
    });

    return {
      head: result.head,
      initialized: true,
      revision: result.revision ?? null,
      state,
    };
  }

  await repository.update(() => state, {
    author: actorUid,
    branch: FORMATION_CONTENT_DEFAULT_BRANCH,
    commit: false,
    concurrency: "last-write-wins",
    message,
  });
  const result = await repository.commit({
    author: actorUid,
    branch: FORMATION_CONTENT_DEFAULT_BRANCH,
    message,
  });

  return {
    head: result.head,
    initialized: false,
    revision: result.created ? result.revision : null,
    state,
  };
}

async function ensureRepositoryInitialized({
  actorUid,
  message,
  repo,
  state,
}: {
  readonly actorUid: string;
  readonly message: string;
  readonly repo: FormationContentRepository;
  readonly state: FormationContentState;
}): Promise<boolean> {
  try {
    await repo.getHead({ branch: FORMATION_CONTENT_DEFAULT_BRANCH });
    return false;
  } catch (error: unknown) {
    if (!(error instanceof BranchNotFoundError)) {
      throw error;
    }
  }

  try {
    await repo.init({
      author: actorUid,
      branch: FORMATION_CONTENT_DEFAULT_BRANCH,
      initialState: state,
      message,
    });
    return true;
  } catch (error: unknown) {
    if (error instanceof RepositoryAlreadyExistsError) {
      return false;
    }

    throw error;
  }
}

async function estimateFormationContentStorage(
  formationId: string,
): Promise<RepositoryStorageEstimate | null> {
  const persistence = createFormationContentPersistence();

  if (!persistence.estimateStorage) {
    return null;
  }

  return persistence.estimateStorage({
    adapterSpecific: true,
    includeBlobs: true,
    includeBranches: true,
    includeHeads: true,
    includeRevisions: true,
    includeTags: true,
    repoId: formationId,
  });
}

async function isFormationContentRevisionReferencedBySession({
  formationId,
  revision,
}: {
  readonly formationId: string;
  readonly revision: RevisionNumber;
}): Promise<boolean> {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  const sourcePath = `formationContentVcs/${formationId}/revisions/${revision}`;
  const [fullContentSnapshot, tagResourceSnapshot] = await Promise.all([
    getDocs(
      query(
        collectionGroup(firestore, "content"),
        where("sourcePath", "==", sourcePath),
      ),
    ),
    getDocs(
      query(
        collectionGroup(firestore, "resources"),
        where("sourcePath", "==", sourcePath),
      ),
    ),
  ]);

  return !fullContentSnapshot.empty || !tagResourceSnapshot.empty;
}

function deckSnapshot(deck: Deck): FormationContentState["decks"][string] {
  return {
    authorUid: deck.authorUid,
    createdAtIso: toIso(deck.createdAt),
    currentPublishedVersionId: deck.currentPublishedVersionId,
    deckId: deck.id,
    exerciseRefs: deck.exerciseRefs,
    formationId: deck.formationId,
    pageCount: deck.pageCount,
    qualiopiQuestionnaireModelRefs:
      readQualiopiQuestionnaireModelRefs(deck),
    questionnaireRefs: deck.questionnaireRefs,
    source: deck.draftContent,
    sourceFormat: "yaml",
    title: deck.title,
    updatedAtIso: toIso(deck.updatedAt),
  };
}

function questionnaireSnapshot(
  questionnaire: Questionnaire,
): FormationContentState["questionnaires"][string] {
  return {
    authorUid: questionnaire.authorUid,
    commentedCorrectAnswer: questionnaire.commentedCorrectAnswer,
    correctionDeckFragment: questionnaire.correctionDeckFragment,
    createdAtIso: toIso(questionnaire.createdAt),
    currentPublishedVersionId: questionnaire.currentPublishedVersionId,
    formationId: questionnaire.formationId,
    questionnaireId: questionnaire.id,
    source: questionnaire.draftDefinition,
    sourceFormat: "yaml",
    title: questionnaire.title,
    updatedAtIso: toIso(questionnaire.updatedAt),
    usage: readQuestionnaireUsage(questionnaire),
  };
}

function exerciseSnapshot(
  exercise: Exercise,
): FormationContentState["exercises"][string] {
  return {
    authorUid: exercise.authorUid,
    correctionDeckFragment: exercise.correctionDeckFragment,
    correctionMarkdown: exercise.correctionMarkdown,
    createdAtIso: toIso(exercise.createdAt),
    currentPublishedVersionId: exercise.currentPublishedVersionId,
    exerciseId: exercise.id,
    formationId: exercise.formationId,
    responseMode: exercise.responseMode,
    source: exercise.draftStatement,
    sourceFormat: "markdown",
    title: exercise.title,
    updatedAtIso: toIso(exercise.updatedAt),
  };
}

function readQualiopiQuestionnaireModelRefs(
  deck: Deck,
): QualiopiQuestionnaireModelRef[] {
  const rawRefs = (deck as DeckWithQualiopiRefs).qualiopiQuestionnaireModelRefs;

  if (!Array.isArray(rawRefs)) {
    return [];
  }

  return rawRefs.flatMap((rawRef) => {
    const data = asRecord(rawRef);
    const modelId = typeof data.modelId === "string" ? data.modelId.trim() : "";

    if (!modelId) {
      return [];
    }

    const usageResult = questionnaireUsageSchema.safeParse(data.usage);
    const sourceResult = qualiopiQuestionnaireModelRefSchema.shape.source.safeParse(
      data.source,
    );

    return [
      {
        label: typeof data.label === "string" ? data.label : null,
        modelId,
        required: data.required === true,
        source: sourceResult.success ? sourceResult.data : "deck",
        usage: usageResult.success ? usageResult.data : "pedagogical",
      },
    ];
  });
}

function readQuestionnaireUsage(
  questionnaire: Questionnaire,
): QuestionnaireUsage {
  const result = questionnaireUsageSchema.safeParse(
    (questionnaire as QuestionnaireWithUsage).usage,
  );

  return result.success ? result.data : "pedagogical";
}

function toIso(value: Date | null): string | null {
  return value ? value.toISOString() : null;
}

function sanitizeTagPart(value: string): string {
  const normalized = value.replace(/[^A-Za-z0-9_-]+/g, "_");
  return normalized.length > 0 ? normalized.slice(0, 80) : "unknown";
}
